import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Card, CardContent, Typography, Grid, Chip, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Pagination, TextField, Button, useTheme, useMediaQuery,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import { ArrowForwardRounded, Visibility, CheckCircle, Cancel } from "@mui/icons-material";
import Swal from "sweetalert2";
import { listBranchExpenses, approveBranchExpense, rejectBranchExpense } from "api/financial";
import ExpenseDetailDialog from "./ExpenseDetailDialog";
import RejectReasonDialog from "./RejectReasonDialog";
import BentoCard from "components/bento/BentoCard";

const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const fmt = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const money = (n, c) => `${SYM[c] || ""}${fmt(n)}`;
const MONO = "'Space Grotesk', 'Noto Sans Lao', ui-monospace, monospace";
const CARD_SHADOW = "0 1px 2px rgba(20,18,30,.04),0 16px 32px -24px rgba(20,18,30,.18)";
const STATUS_OPTIONS = ["pending_approval", "completed", "rejected", "all"];
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

// Approvals list for staff-recorded branch cash expenses over EXPENSE_THRESHOLDS —
// mirrors ShippingApprovals (approvals.jsx) but for BranchCashExpense instead of
// ShippingPayment: no customer, just station + reason + amount.
export default function BranchExpenseApprovalsTab({ onChanged }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [statusFilter, setStatusFilter] = useState("pending_approval");
  const [stationId, setStationId] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const res = await listBranchExpenses({ status: statusFilter, stationId: stationId || undefined, page: p, limit: 20 });
    setRows(res && res.success ? res.data : []);
    setPagination((res && res.pagination) || { page: 1, pages: 1, total: 0 });
    setLoading(false);
  }, [statusFilter, stationId]);
  useEffect(() => { setPage(1); load(1); }, [statusFilter, stationId, load]);

  const handlePageChange = (e, p) => { setPage(p); load(p); };

  const handleApprove = async (id) => {
    setBusy(true);
    const res = await approveBranchExpense(id);
    setBusy(false);
    Swal.fire({ icon: res.success ? "success" : "error", text: res.message, timer: 2000, toast: true, position: "top-right", showConfirmButton: false });
    if (res.success) setDetailId(null);
    load(page);
    onChanged && onChanged();
  };
  const doReject = async (reason) => {
    setBusy(true);
    const res = await rejectBranchExpense(rejectId, reason || "");
    setBusy(false);
    Swal.fire({ icon: res.success ? "success" : "error", text: res.message, timer: 2000, toast: true, position: "top-right", showConfirmButton: false });
    if (res.success) { setRejectId(null); setDetailId(null); }
    load(page);
    onChanged && onChanged();
  };

  return (
    <>
      <Box sx={{ display: { xs: "grid", md: "flex" }, gridTemplateColumns: { xs: "1fr 1fr" }, alignItems: "center", gap: "12px", mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ width: { xs: "100%", md: "auto" }, minWidth: { md: 170 } }}>
          <InputLabel>{t("financial.status")}</InputLabel>
          <Select value={statusFilter} label={t("financial.status")} onChange={(e) => setStatusFilter(e.target.value)}>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>{t(`financial.${s === "pending_approval" ? "pending" : s}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" label={t("financial.station")} value={stationId}
          onChange={(e) => setStationId(e.target.value)}
          sx={{ width: { xs: "100%", md: "auto" }, minWidth: { md: 160 } }}
        />
        <Button variant="outlined" color="secondary" sx={{ width: { xs: "100%", md: "auto" } }} onClick={() => setStationId("")}>
          {t("common.clear")}
        </Button>
        <Box sx={{ gridColumn: { xs: "1 / -1" }, ml: { md: "auto" }, textAlign: { xs: "right" }, fontSize: "12.5px", color: "#a8a3b4", fontWeight: 600 }}>
          {pagination.total || 0} {t("financial.results")}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ p: 3 }}><CircularProgress color="secondary" /></Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>
          {t(statusFilter === "pending_approval" ? "financial.noPending" : "financial.noData")}
        </Typography>
      ) : isMobile ? (
        <Grid container spacing={2}>
          {rows.map((exp) => (
            <Grid item xs={12} md={6} key={exp._id}>
              <Card
                elevation={0}
                onClick={() => setDetailId(exp._id)}
                sx={{ cursor: "pointer", borderRadius: "20px", border: "1px solid #ece9f1", boxShadow: CARD_SHADOW, transition: "transform .18s, box-shadow .18s, border-color .18s", "&:hover": { transform: "translateY(-3px)", borderColor: "#d9cffb", boxShadow: "0 1px 2px rgba(20,18,30,.05),0 22px 40px -22px rgba(86,40,160,.4)" } }}
              >
                <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", color: "#a8a3b4" }}>{t("branchCash.amount")}</Typography>
                      <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 26, lineHeight: 1.15, color: "#c0343a" }}>−{money(exp.amount, exp.currency)}</Typography>
                    </Box>
                    <Chip size="small" color={exp.status === "completed" ? "success" : exp.status === "rejected" ? "error" : "warning"} label={t(`financial.${exp.status === "pending_approval" ? "pending" : exp.status}`)} sx={{ fontWeight: 700, flexShrink: 0 }} />
                  </Box>
                  <Typography sx={{ mt: 1.5, fontSize: 13.5, fontWeight: 600, color: "#2c2937" }}>{exp.reason}</Typography>
                  <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}>
                    {[
                      { label: t("financial.station"), value: exp.stationId },
                      { label: t("branchCash.requestedBy"), value: exp.requestedBy?.name },
                    ].map((m, i) => (
                      <Box key={i} sx={{ p: 1.25, borderRadius: "12px", background: "#faf9fc", border: "1px solid #f0eef5" }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#a8a3b4" }}>{m.label}</Typography>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#2c2937", mt: 0.25, wordBreak: "break-word" }}>{m.value || "—"}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px solid #f1eff5", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5, color: "#6d28d9" }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{t("financial.viewDetails")}</Typography>
                    <ArrowForwardRounded sx={{ fontSize: 16 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <BentoCard bodySx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("branchCash.expenseReason")}</TableCell>
                  <TableCell>{t("branchCash.amount")}</TableCell>
                  <TableCell>{t("financial.status")}</TableCell>
                  <TableCell>{t("financial.station")}</TableCell>
                  <TableCell>{t("branchCash.requestedBy")}</TableCell>
                  <TableCell>{t("financial.date")}</TableCell>
                  <TableCell>{t("financial.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((exp) => (
                  <TableRow key={exp._id} hover>
                    <TableCell><Typography variant="body2" sx={{ maxWidth: 260 }}>{exp.reason}</Typography></TableCell>
                    <TableCell><Typography variant="body1" fontWeight="bold" color="error.main">−{money(exp.amount, exp.currency)}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small" color={exp.status === "completed" ? "success" : exp.status === "rejected" ? "error" : "warning"} label={t(`financial.${exp.status === "pending_approval" ? "pending" : exp.status}`)} sx={{ textTransform: "capitalize" }} />
                    </TableCell>
                    <TableCell><Typography variant="body2">{exp.stationId}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{exp.requestedBy?.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{fmtDate(exp.createdAt)}</Typography></TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Button variant="outlined" color="secondary" size="small" startIcon={<Visibility />} onClick={() => setDetailId(exp._id)}>
                          {t("financial.details")}
                        </Button>
                        {exp.status === "pending_approval" && (
                          <>
                            <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => handleApprove(exp._id)}>{t("financial.approve")}</Button>
                            <Button size="small" variant="contained" color="error" startIcon={<Cancel />} onClick={() => setRejectId(exp._id)}>{t("financial.reject")}</Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </BentoCard>
      )}

      {pagination.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Pagination color="secondary" count={pagination.pages} page={page} onChange={handlePageChange} />
        </Box>
      )}

      <ExpenseDetailDialog
        open={!!detailId}
        expenseId={detailId}
        mode="financial"
        busy={busy}
        onClose={() => setDetailId(null)}
        onApprove={handleApprove}
        onReject={(id) => setRejectId(id)}
      />
      <RejectReasonDialog open={!!rejectId} busy={busy} onClose={() => setRejectId(null)} onConfirm={doReject} />
    </>
  );
}
